import { useState } from "react";
import { Activity, Plus, Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "h-12 rounded-lg border border-slate-300 px-3 text-base font-vitals font-bold focus:outline-none focus:ring-2 focus:ring-[#005EB8]";
const nowTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const fmtEcgDate = (dt) => {
  if (!dt) return "";
  const d = new Date(dt);
  return d.toDateString() === new Date().toDateString()
    ? "today"
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

export default function EcgSection({ patient, onChanged }) {
  const [time, setTime] = useState(nowTime);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState("");
  const ecgs = patient.ecgs || [];

  const add = async () => {
    if (!time) { toast.error("Enter the time the ECG was done"); return; }
    setAdding(true);
    try {
      await api.post(`/patients/${patient.id}/ecgs`, { time });
      toast.success("ECG added");
      setTime(nowTime());
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  const upload = async (eid, file) => {
    if (!file) return;
    setUploading(eid);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/patients/${patient.id}/ecgs/${eid}/photo`, fd);
      toast.success("ECG photo added");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading("");
    }
  };

  const remove = async (eid) => {
    try {
      await api.delete(`/patients/${patient.id}/ecgs/${eid}`);
      toast.success("ECG deleted");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6" data-testid="ecg-section">
      <h2 className="font-heading text-lg font-bold text-[#0F2547] flex items-center gap-2 mb-1">
        <Activity className="h-5 w-5 text-[#D5281B]" /> ECGs
      </h2>
      <p className="text-xs text-slate-500 mb-4">Record the time of each ECG and attach a photo of the trace.</p>
      <div className="flex gap-2 mb-4">
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="ecg-time"
          title="Auto-set to now — change it if you're logging an earlier ECG" className={`${inputCls} w-36`} />
        <button onClick={add} disabled={adding} data-testid="ecg-add"
          className="h-12 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 transition-colors text-white text-sm font-semibold inline-flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> Add ECG
        </button>
      </div>
      <p className="text-[10px] text-slate-400 -mt-2 mb-3">Time is set automatically to now — just tap Add ECG, like observations.</p>
      {ecgs.length === 0 ? (
        <p className="text-sm text-slate-500 py-4 text-center" data-testid="no-ecgs-message">No ECGs recorded.</p>
      ) : (
        <div className="space-y-3" data-testid="ecg-list">
          {ecgs.map((e) => (
            <div key={e.id} className="rounded-xl border border-slate-200 p-4" data-testid={`ecg-entry-${e.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="font-vitals text-sm font-bold text-[#0F2547]">{e.time}</span>
                <span className="text-[11px] text-slate-400 flex-1">{fmtEcgDate(e.created_at)} · by {e.recorded_by_name}</span>
                <button onClick={() => remove(e.id)} data-testid={`ecg-delete-${e.id}`} aria-label="Delete this ECG"
                  title="Delete this ECG"
                  className="h-8 w-8 grid place-items-center rounded-lg text-slate-300 hover:text-[#D5281B] hover:bg-red-50 transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {e.photo_path ? (
                <img src={`${API}/api/patients/${patient.id}/ecgs/${e.id}/photo-image`} alt={`ECG at ${e.time}`}
                  data-testid={`ecg-photo-${e.id}`} className="max-h-64 rounded-lg border border-slate-200" />
              ) : (
                <div className="flex flex-wrap gap-2">
                  <label data-testid={`ecg-camera-label-${e.id}`}
                    className="inline-flex items-center gap-1.5 h-11 px-4 rounded-lg border-2 border-dashed border-slate-300 text-xs font-bold text-[#005EB8] hover:border-[#005EB8] transition-colors cursor-pointer">
                    <Camera className="h-4 w-4" /> {uploading === e.id ? "Uploading…" : "Take photo"}
                    <input type="file" accept="image/*" capture="environment" className="hidden"
                      data-testid={`ecg-camera-${e.id}`} onChange={(ev) => { upload(e.id, ev.target.files[0]); ev.target.value = ""; }} />
                  </label>
                  <label data-testid={`ecg-upload-label-${e.id}`}
                    className="inline-flex items-center gap-1.5 h-11 px-4 rounded-lg border-2 border-dashed border-slate-300 text-xs font-bold text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors cursor-pointer">
                    <Plus className="h-4 w-4" /> Upload photo
                    <input type="file" accept="image/*" className="hidden"
                      data-testid={`ecg-upload-${e.id}`} onChange={(ev) => { upload(e.id, ev.target.files[0]); ev.target.value = ""; }} />
                  </label>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
